import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Spinner } from '../shared/Spinner';
import type { Invoice } from '../../types';

/**
 * Panneau d'état de l'archivage Dropbox pour une facture déjà créée dans Zoho.
 * Le bouton « Réessayer l'archivage » relance UNIQUEMENT l'upload Dropbox
 * (POST /:id/archive) — il ne recrée jamais la bill Zoho.
 *
 * Les NDF (source=dropbox_ndf_dominique) ne sont jamais archivées (le PDF est
 * déjà dans Dropbox) : statut null → simple note informative, pas de bouton.
 */
export function ArchiveStatusPanel({ invoice }: { invoice: Invoice }) {
  const qc = useQueryClient();
  const toast = useToast();

  const retry = useMutation({
    mutationFn: () => invoicesApi.archive(invoice.id),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['invoice', invoice.id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      if (resp.dropbox_archive_status === 'success') {
        toast.success('Facture archivée dans Dropbox');
      } else if (resp.dropbox_archive_status === 'partial') {
        toast.error(`Archivage partiel : ${resp.archive_error ?? resp.accountant_error ?? ''}`);
      } else {
        toast.error(
          `Archivage toujours en échec : ${resp.archive_error ?? resp.accountant_error ?? 'erreur inconnue'}`,
        );
      }
    },
    onError: (err) => toast.error(`Réessai échoué : ${String(err)}`),
  });

  const status = invoice.dropbox_archive_status;
  const isNdf = invoice.source?.startsWith('dropbox_ndf_') ?? false;

  // NDF : pas d'archivage attendu.
  if (isNdf) {
    return (
      <div className="rounded-md bg-slate-50 ring-1 ring-inset ring-slate-200 px-3 py-2 text-sm text-slate-600">
        Note de frais : pas d'archivage automatique (le PDF est déjà dans Dropbox).
      </div>
    );
  }

  // Facture créée avant l'ajout du suivi (status null) mais déjà archivée :
  // on l'affiche comme un succès legacy plutôt qu'une fausse alerte.
  const legacyArchived = status == null && Boolean(invoice.dropbox_archive_path);

  if (status === 'success' || legacyArchived) {
    return (
      <div className="rounded-md bg-emerald-50 ring-1 ring-inset ring-emerald-200 px-3 py-2 text-sm text-emerald-800">
        <div className="font-medium">✓ Archivée dans Dropbox</div>
        {invoice.dropbox_archive_path && (
          <div className="mt-1 font-mono text-xs text-emerald-700 break-all">
            {invoice.dropbox_archive_path}
          </div>
        )}
        {invoice.dropbox_accountant_path && (
          <div className="mt-0.5 font-mono text-xs text-emerald-700 break-all">
            {invoice.dropbox_accountant_path}
          </div>
        )}
      </div>
    );
  }

  // 'failed' | 'partial' | null (créée avant l'ajout du suivi d'archivage).
  const isFailure = status === 'failed' || status === 'partial';
  return (
    <div
      className={`rounded-md px-3 py-2 text-sm ring-1 ring-inset ${
        isFailure
          ? 'bg-red-50 ring-red-200 text-red-800'
          : 'bg-amber-50 ring-amber-200 text-amber-800'
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium">
            {status === 'partial'
              ? '⚠️ Archivage partiel dans Dropbox'
              : status === 'failed'
                ? '⚠️ Archivage Dropbox échoué'
                : "Statut d'archivage inconnu"}
          </div>
          {invoice.dropbox_archive_error && (
            <div className="mt-1 text-xs break-words">{invoice.dropbox_archive_error}</div>
          )}
          {/* Chemins déjà obtenus (cas partiel). */}
          {invoice.dropbox_archive_path && (
            <div className="mt-1 font-mono text-xs break-all opacity-80">
              {invoice.dropbox_archive_path}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => retry.mutate()}
          disabled={retry.isPending}
          className="shrink-0 px-3 py-1.5 rounded-md font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {retry.isPending && <Spinner />}
          Réessayer l'archivage
        </button>
      </div>
    </div>
  );
}
